import { cn } from '~/utils/cn';

import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';
import type { propsIconsNames } from '../../atoms/Icon/interfaces';

type Variant = 'critical' | 'warning' | 'success' | 'info';

interface PropsAlertCallout {
  variant?: Variant;
  title: string;
  description?: string;
  ctaText?: string;
  onClickCta?: () => void;
  className?: string;
}

const VARIANTS: Record<
  Variant,
  {
    bg: string;
    iconBg: string;
    icon: propsIconsNames;
    iconColor: string;
    titleClass: string;
    descClass: string;
    ctaBg: string;
    ctaText: string;
    label: string;
  }
> = {
  critical: {
    bg: 'bg-warning-05',
    iconBg: 'bg-warning-02',
    icon: 'icon-circle-alert',
    iconColor: '#BF3333',
    titleClass: 'color-intense-01',
    descClass: 'color-intense-01',
    ctaBg: 'bg-intense-01',
    ctaText: 'color-warning-05',
    label: 'ATENÇÃO',
  },
  warning: {
    bg: 'bg-alert-01 border border-alert-04',
    iconBg: 'bg-alert-02',
    icon: 'icon-clock',
    iconColor: '#E6A82E',
    titleClass: 'color-complement-01',
    descClass: 'color-intense-06',
    ctaBg: 'bg-alert-05',
    ctaText: 'color-intense-01',
    label: 'PRÓXIMO DE VENCER',
  },
  success: {
    bg: 'bg-success-01 border border-success-04',
    iconBg: 'bg-success-04',
    icon: 'icon-circle-check',
    iconColor: '#FFFFFF',
    titleClass: 'color-complement-01',
    descClass: 'color-intense-06',
    ctaBg: 'bg-success-04',
    ctaText: 'color-intense-01',
    label: 'TUDO EM DIA',
  },
  info: {
    bg: 'bg-info-01 border border-info-03',
    iconBg: 'bg-info-03',
    icon: 'icon-circle-alert',
    iconColor: '#FFFFFF',
    titleClass: 'color-complement-01',
    descClass: 'color-intense-06',
    ctaBg: 'bg-info-04',
    ctaText: 'color-intense-01',
    label: 'INFORMAÇÃO',
  },
};

export default function AlertCallout({
  variant = 'critical',
  title,
  description,
  ctaText,
  onClickCta,
  className,
}: PropsAlertCallout) {
  const v = VARIANTS[variant];

  return (
    <div className={cn('rounded-small p-small', v.bg, className)}>
      <div className="flex items-start gap-smaller">
        <div
          className={cn(
            'w-[36px] h-[36px] rounded-[10px] flex items-center justify-center shrink-0',
            v.iconBg,
          )}
        >
          <Icon name={v.icon} size={18} color={v.iconColor} rounded={false} />
        </div>
        <div className="flex-1 min-w-0">
          <Text className={cn('text-captionLG uppercase', v.descClass)}>
            {v.label}
          </Text>
          <Text className={cn('mt-quark text-paragraphLG', v.titleClass)}>
            {title}
          </Text>
          {description ? (
            <Text className={cn('mt-quark text-bodySM', v.descClass)}>
              {description}
            </Text>
          ) : null}
        </div>
      </div>

      {ctaText && onClickCta ? (
        <button
          type="button"
          onClick={onClickCta}
          className={cn(
            'mt-smaller w-full flex items-center justify-center rounded-[12px] py-smaller hover:opacity-90',
            v.ctaBg,
          )}
        >
          <Text className={cn('text-paragraphMD', v.ctaText)}>{ctaText}</Text>
        </button>
      ) : null}
    </div>
  );
}
